import { watch, onUnmounted, type Ref } from "vue";
import type { Map } from "mapbox-gl";
import type { FeatureCollection } from "geojson";

export const POINT_CIRCLE_RADIUS = 8;
export const POINT_HIT_STROKE = 6;
export const MAP_POINT_COLOR = "#56F7DA";
export const MAP_LABEL_COLOR = "#162340";

export const EMPTY_FEATURE_COLLECTION: FeatureCollection = { type: "FeatureCollection", features: [] };

interface StyleScopedMap {
  on(type: "style.load", listener: () => void): unknown;
  getLayer(id: string): unknown;
  getSource(id: string): unknown;
  removeLayer(id: string): unknown;
  removeSource(id: string): unknown;
}

export function removeLayersIfPresent(m: Pick<Map, "getLayer" | "removeLayer">, ...ids: string[]) {
  for (const id of ids) {
    if (m.getLayer(id)) m.removeLayer(id);
  }
}

export function removeSourcesIfPresent(m: Pick<Map, "getSource" | "removeSource">, ...ids: string[]) {
  for (const id of ids) {
    if (m.getSource(id)) m.removeSource(id);
  }
}

export function bindPointerCursor(m: Map, ...layerIds: string[]) {
  const setPointer = () => {
    m.getCanvas().style.cursor = "pointer";
  };
  const resetCursor = () => {
    m.getCanvas().style.cursor = "";
  };
  for (const id of layerIds) {
    m.on("mouseenter", id, setPointer);
    m.on("mouseleave", id, resetCursor);
  }
  return () => {
    for (const id of layerIds) {
      m.off("mouseenter", id, setPointer);
      m.off("mouseleave", id, resetCursor);
    }
  };
}

// `setStyle` efface sources et layers mais laisse les écouteurs, et refire `style.load` (pas `load`).
export function useAppMapStyleLayers<M extends StyleScopedMap>(map: Ref<M | undefined>, setup: (m: M) => () => void) {
  let teardown: (() => void) | undefined;

  watch(
    map,
    (m) => {
      teardown = undefined;
      if (!m) return;
      m.on("style.load", () => {
        teardown?.();
        teardown = setup(m);
      });
    },
    { immediate: true }
  );

  onUnmounted(() => {
    teardown?.();
  });
}
